import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import { RecipeDesignerIcon } from '../Icons/RecipeDesignerIcon';
import { RecipeResultsIcon } from '../Icons/RecipeResultsIcon';
import { colors, spacing, radius, typography, transitions } from '../../containers/RecipeDesigner/styles/designTokens';

const NavItem = styled(Box)(({ selected }) => ({
  padding: `${spacing.md} ${spacing.md}`, // 16px padding all around
  marginLeft: 0,
  marginRight: 0,
  borderRadius: radius.md,
  cursor: 'pointer',
  transition: transitions.default,
  backgroundColor: selected ? colors.accent.primaryLight : 'transparent',
  borderLeft: selected ? `3px solid ${colors.accent.primary}` : '3px solid transparent',
  '&:hover': {
    backgroundColor: selected
      ? colors.accent.primaryLight
      : colors.background.subtle,
  },
  '&:focus-visible': {
    outline: `2px solid ${colors.border.focus}`,
    outlineOffset: '2px',
  },
}));

/**
 * Hero Navigation Section
 *
 * Always-visible, prominent section for Recipe Designer and Recipe Results.
 * These are the primary workflow features we want to drive adoption for.
 *
 * Features:
 * - Clean, minimal design matching RecipeDesigner quality
 * - Subtle status indicators
 * - Never hidden, always accessible
 * - Highlights current selection
 */
export default function HeroNavSection({
  activeRecipeCount = 0,
  runningExecutionCount = 0,
  recentExecutionCount = 0,
  onNavigate,
  currentPath = '',
}) {
  // Match exact route or subroutes (e.g., /recipe-designer or /recipe-designer/123)
  const isDesignerActive = currentPath === '/recipe-designer' || currentPath.startsWith('/recipe-designer/');
  const isResultsActive = currentPath === '/recipe-results' || currentPath.startsWith('/recipe-results/');

  const handleNavigate = (path, event) => {
    if (onNavigate) {
      onNavigate(path, null, event);
    }
  };

  return (
    <Box sx={{ py: 1.5 }}>
      <Typography
        sx={{
          ...typography.sectionTitle,
          mb: 1.5,
          pl: 2, // 16px left padding
          pr: 2,
          display: 'block',
        }}
      >
        RECIPES
      </Typography>

      <Stack spacing={1}>
        {/* Recipe Designer */}
        <NavItem
          selected={isDesignerActive}
          onClick={(e) => handleNavigate('/recipe-designer', e)}
          tabIndex={0}
          role="button"
          aria-label="Recipe Designer"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleNavigate('/recipe-designer', e);
            }
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <RecipeDesignerIcon
              color={isDesignerActive ? colors.accent.primary : colors.text.tertiary}
              size={18}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: isDesignerActive ? colors.accent.primary : colors.text.primary,
                  lineHeight: 1.3,
                }}
              >
                Recipe Designer
              </Typography>
              {activeRecipeCount > 0 && (
                <Typography
                  sx={{
                    fontSize: '0.6875rem',
                    color: colors.text.tertiary,
                    mt: 0.25,
                    lineHeight: 1.2,
                  }}
                >
                  {activeRecipeCount} active
                </Typography>
              )}
            </Box>
          </Stack>
        </NavItem>

        {/* Recipe Results */}
        <NavItem
          selected={isResultsActive}
          onClick={(e) => handleNavigate('/recipe-results', e)}
          tabIndex={0}
          role="button"
          aria-label="Recipe Results"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleNavigate('/recipe-results', e);
            }
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <RecipeResultsIcon
              color={isResultsActive ? colors.accent.primary : colors.text.tertiary}
              size={18}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: isResultsActive ? colors.accent.primary : colors.text.primary,
                  lineHeight: 1.3,
                }}
              >
                Recipe Results
              </Typography>
              {(runningExecutionCount > 0 || recentExecutionCount > 0) && (
                <Typography
                  sx={{
                    fontSize: '0.6875rem',
                    color: colors.text.tertiary,
                    mt: 0.25,
                    lineHeight: 1.2,
                  }}
                >
                  {runningExecutionCount > 0
                    ? `${runningExecutionCount} running`
                    : `${recentExecutionCount} recent`}
                </Typography>
              )}
            </Box>
          </Stack>
        </NavItem>
      </Stack>
    </Box>
  );
}
